import Box from "@mui/material/Box"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"

import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import SectionSeparator from "../../customer/components/SectionSeparator"
import {
  adminLiquidTableFooterSx,
  adminLiquidTableShellSx,
  adminLiquidToolbarInnerSx,
} from "../adminStyles"
import { adminTableEdgeInsetX } from "./AdminHeaderTableCell"

const AdminDataTable = styled(Table)(({ theme }) => ({
  width: "100%",
  minWidth: 520,
  tableLayout: "auto",
  borderCollapse: "separate",
  borderSpacing: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 640,
  },
  [theme.breakpoints.up("lg")]: {
    minWidth: 720,
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    height: "auto",
  },
  "& .MuiTableHead-root .MuiTableCell-root:first-of-type, & .MuiTableBody-root .MuiTableCell-root:first-of-type":
    {
      pl: adminTableEdgeInsetX,
    },
  "& .MuiTableHead-root .MuiTableCell-root:last-of-type, & .MuiTableBody-root .MuiTableCell-root:last-of-type":
    {
      pr: adminTableEdgeInsetX,
    },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: "rgba(239, 235, 251, 0.4)",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "rgba(124, 58, 237, 0.08)",
  },
  "& .MuiTableCell-root": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    verticalAlign: "middle",
  },
}))

export const AdminTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s ease",
}))

function LoadingRows({ columns, pageSize }) {
  return Array.from({ length: Math.min(pageSize, 5) }).map((_, index) => (
    <AdminTableRow key={index}>
      {Array.from({ length: columns }).map((__, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="text" height={28} />
        </TableCell>
      ))}
    </AdminTableRow>
  ))
}

export default function AdminListingTable({
  tableHead,
  children,
  isLoading,
  rows,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  totalResultsSize,
  columnCount,
  toolbar,
}) {
  return (
    <SectionSeparator>
      <ConditionalPurpleLinearProgress isLoading={isLoading} />
      <Column sx={adminLiquidTableShellSx}>
        {toolbar ? <Box sx={adminLiquidToolbarInnerSx}>{toolbar}</Box> : null}
        <ScrollableTableContainer
          sx={{
            maxHeight: {
              xs: "calc(100vh - 380px)",
              sm: "calc(100vh - 360px)",
              md: "calc(100vh - 340px)",
            },
            WebkitOverflowScrolling: "touch",
          }}
        >
          <AdminDataTable size="small" stickyHeader>
            <TableHead>{tableHead}</TableHead>
            <TableBody>
              {isLoading ? (
                <LoadingRows columns={columnCount} pageSize={pageSize} />
              ) : rows.length === 0 ? (
                <NoResultsTableRow colSpan={columnCount} />
              ) : (
                children
              )}
            </TableBody>
          </AdminDataTable>
        </ScrollableTableContainer>
        <Box sx={adminLiquidTableFooterSx}>
          <TablePaginationRow
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            pageSize={pageSize}
            setPageSize={setPageSize}
            tableRows={rows}
            totalResultsSize={totalResultsSize}
            paginationDisabled={isLoading}
          />
        </Box>
      </Column>
    </SectionSeparator>
  )
}

AdminListingTable.propTypes = {
  tableHead: PropTypes.node.isRequired,
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  rows: PropTypes.array.isRequired,
  pageIndex: PropTypes.number.isRequired,
  setPageIndex: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  totalResultsSize: PropTypes.number.isRequired,
  columnCount: PropTypes.number.isRequired,
  toolbar: PropTypes.node,
}
